<template>
  <div class="admin-dashboard">
    <!-- SIDEBAR -->
    <SidebarNav :activeTab="activeTab" @changeTab="changeTab" @toggleSidebar="handleSidebarToggle" />

    <!-- MAIN CONTENT -->
    <main class="main-content" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
      <!-- LOADING SCREEN -->
      <div v-if="loading" class="loading-overlay">
        <div class="loading-spinner"></div>
        <p class="loading-text">Загрузка данных...</p>
      </div>

      <!-- ERROR SCREEN -->
      <div v-else-if="error" class="error-container">
        <div class="error-icon">⚠️</div>
        <p class="error-title">Ошибка загрузки данных</p>
        <p class="error-message">{{ error }}</p>
        <button @click="fetchAllData" class="btn-primary">Повторить попытку</button>
      </div>

      <!-- CONTENT BY TAB -->
      <div v-else>
        <!-- Dashboard Tab -->
        <DashboardContent
          v-if="activeTab === 'dashboard'"
          :orders="orders"
          :harpooners="harpooners"
          @refresh="fetchAllData"
        />

        <!-- Hidden Orders Tab -->
        <HiddenOrdersTab
          v-if="activeTab === 'hidden'"
        />

        <!-- Clients Phone Management Tab -->
        <ClientsPhoneTab
          v-if="activeTab === 'clients'"
          :clients="clients"
          :stats="clientsStats"
          @sync="syncClients"
          @refresh="fetchClientsData"
          @update-phone="updateClientPhone"
          @delete-phone="deleteClientPhone"
        />

        <!-- Users Management Tab -->
        <UsersTab
          v-if="activeTab === 'users'"
        />

        <!-- Settings Tab -->
        <SettingsTab
          v-if="activeTab === 'settings'"
        />
      </div>
    </main>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import axios from 'axios'
import SidebarNav from '@/components/admin/SideBar.vue'
import DashboardContent from '@/components/admin/DashboardContent.vue'
import ClientsPhoneTab from '@/components/admin/ClientsPhoneTab.vue'
import HiddenOrdersTab from '@/components/admin/HiddenOrdersTab.vue'
import UsersTab from '@/components/admin/UsersTab.vue'
import SettingsTab from '@/components/admin/SettingsTab.vue'

const API_ORDERS = 'https://mpapi.premside.uz/api/status'
const API_HARPOONERS = 'https://mpapi.premside.uz/api/speed?IDmanager=ALL'
const API_CLIENTS_BASE = 'https://wawh.premside.uz'

export default {
  name: 'AdminPage',
  components: {
    SidebarNav,
    DashboardContent,
    ClientsPhoneTab,
    HiddenOrdersTab,
    UsersTab,
    SettingsTab
  },
  setup() {
    const activeTab = ref('dashboard')
    const loading = ref(true)
    const error = ref(null)
    const sidebarCollapsed = ref(true)

    // Dashboard data
    const orders = ref([])
    const harpooners = ref([])

    // Clients data
    const clients = ref([])
    const clientsStats = ref({
      totalClients: 0,
      withPhone: 0,
      withoutPhone: 0,
      phonesNeeded: 0,
      processed: 0,
      processedToday: 0
    })

    let timer = null

    /**
     * Загрузка заказов (для Dashboard)
     */
    const fetchOrders = async () => {
      try {
        const response = await axios.get(API_ORDERS)
        orders.value = response.data.message?.data || response.data.data || []
      } catch (err) {
        console.error('Error fetching orders:', err)
        throw err
      }
    }

    /**
     * Загрузка гарпунщиков (для Dashboard)
     */
    const fetchHarpooners = async () => {
      try {
        const response = await axios.get(API_HARPOONERS)
        const result = response.data

        if (result.status === 'ok' && result.data) {
          harpooners.value = result.data
        }
      } catch (err) {
        console.error('Error fetching harpooners:', err)
        // Не бросаем ошибку, так как это не критично
      }
    }

    /**
     * Загрузка статистики клиентов (для Clients Tab)
     */
    const fetchClientsStats = async () => {
      try {
        const response = await axios.get(`${API_CLIENTS_BASE}/monitor/stats`)
        const data = response.data

        clientsStats.value.processed = data.total_processed || 0
        clientsStats.value.processedToday = data.processed_today || 0
        clientsStats.value.phonesNeeded = data.phones_needed || 0
      } catch (err) {
        console.error('Error fetching clients stats:', err)
      }
    }

    /**
     * Загрузка клиентов (для Clients Tab)
     */
    const fetchClients = async () => {
      try {
        const response = await axios.get(`${API_CLIENTS_BASE}/api/clients?per_page=10000`)
        const allClients = response.data.clients || []

        clients.value = allClients

        // Обновляем статистику
        clientsStats.value.totalClients = allClients.length
        clientsStats.value.withPhone = allClients.filter(c => c.Phone).length
        clientsStats.value.withoutPhone = allClients.filter(c => !c.Phone).length
      } catch (err) {
        console.error('Error fetching clients:', err)
        // Не бросаем ошибку - устанавливаем пустой массив
        clients.value = []
        clientsStats.value.totalClients = 0
        clientsStats.value.withPhone = 0
        clientsStats.value.withoutPhone = 0
      }
    }

    /**
     * Загрузка данных для Dashboard
     */
    const fetchDashboardData = async () => {
      await Promise.all([
        fetchOrders(),
        fetchHarpooners()
      ])
    }

    /**
     * Загрузка данных для Clients Tab
     */
    const fetchClientsData = async () => {
      await Promise.all([
        fetchClientsStats(),
        fetchClients()
      ])
    }

    /**
     * Загрузка всех данных (в зависимости от активной вкладки)
     */
    const fetchAllData = async () => {
      error.value = null

      try {
        if (activeTab.value === 'dashboard') {
          await fetchDashboardData()
        } else if (activeTab.value === 'clients') {
          await fetchClientsData()
        }
      } catch (err) {
        console.error('Error fetching data:', err)
        error.value = err.response?.data?.message || err.message || 'Не удалось загрузить данные'
      } finally {
        loading.value = false
      }
    }

    /**
     * Синхронизация клиентов из Access
     */
    const syncClients = async () => {
      try {
        await axios.post(`${API_CLIENTS_BASE}/api/clients/sync`)

        // Подождем 5 секунд и обновим данные
        setTimeout(async () => {
          await fetchClientsData()
        }, 5000)
      } catch (err) {
        console.error('Error syncing clients:', err)
        throw err
      }
    }

    /**
     * Обновление номера телефона клиента
     */
    const updateClientPhone = async ({ clientId, phone }) => {
      try {
        await axios.put(`${API_CLIENTS_BASE}/api/clients/${clientId}/phone`, { phone })

        // Обновляем локальные данные
        const client = clients.value.find(c => c.IDClient === clientId)
        if (client) {
          client.Phone = phone
          client.UpdatedAt = new Date().toISOString()
        }

        await fetchClientsStats()
      } catch (err) {
        console.error('Error updating phone:', err)
        throw err
      }
    }

    /**
     * Удаление номера телефона клиента
     */
    const deleteClientPhone = async (clientId) => {
      try {
        await axios.delete(`${API_CLIENTS_BASE}/api/clients/${clientId}/phone`)

        // Обновляем локальные данные
        const client = clients.value.find(c => c.IDClient === clientId)
        if (client) {
          client.Phone = null
          client.UpdatedAt = new Date().toISOString()
        }

        await fetchClientsStats()
      } catch (err) {
        console.error('Error deleting phone:', err)
        throw err
      }
    }

    /**
     * Переключение вкладок
     */
    const changeTab = async (tab) => {
      activeTab.value = tab
      loading.value = true
      await fetchAllData()
    }

    /**
     * Обработка сворачивания/разворачивания сайдбара
     */
    const handleSidebarToggle = (isCollapsed) => {
      sidebarCollapsed.value = isCollapsed
    }

    onMounted(async () => {
      await fetchAllData()

      // Автообновление каждые 30 секунд
      timer = setInterval(async () => {
        // Не показываем loader при автообновлении
        const tempLoading = loading.value
        loading.value = false

        await fetchAllData()

        loading.value = tempLoading
      }, 30000)
    })

    onUnmounted(() => {
      if (timer) clearInterval(timer)
    })

    return {
      activeTab,
      loading,
      error,
      sidebarCollapsed,
      orders,
      harpooners,
      clients,
      clientsStats,
      fetchAllData,
      syncClients,
      updateClientPhone,
      deleteClientPhone,
      fetchClientsData,
      changeTab,
      handleSidebarToggle
    }
  }
}
</script>

<style scoped>
/* MAIN LAYOUT */
.admin-dashboard {
  display: flex;
  min-height: 100vh;
  background: #f8f9fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.main-content {
  flex: 1;
  margin-left: 260px;
  padding: 30px;
  transition: margin-left 0.3s ease;
}

.main-content.sidebar-collapsed {
  margin-left: 70px;
}

/* LOADING */
.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 20px;
  color: #6b7280;
  font-size: 16px;
}

/* ERROR */
.error-container {
  text-align: center;
  padding: 80px 20px;
}

.error-icon {
  font-size: 80px;
  margin-bottom: 20px;
}

.error-title {
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 10px;
}

.error-message {
  color: #6b7280;
  margin-bottom: 30px;
}

.btn-primary {
  padding: 12px 24px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(99, 102, 241, 0.3);
}

/* RESPONSIVE */
@media (max-width: 1024px) {
  .main-content:not(.sidebar-collapsed) {
    margin-left: 200px;
  }
}

@media (max-width: 768px) {
  .main-content,
  .main-content.sidebar-collapsed {
    margin-left: 0;
    padding: 20px;
  }
}
</style>